import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vShow as _vShow, withDirectives as _withDirectives, createElementVNode as _createElementVNode, isRef as _isRef, toDisplayString as _toDisplayString, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, Teleport as _Teleport } from "vue"

const _hoisted_1 = ["data-dusk"]
const _hoisted_2 = { class: "border-t border-gray-200 mt-6" }
const _hoisted_3 = { class: "grid grid-cols-5 gap-6 mt-6" }
const _hoisted_4 = { class: "grid grid-cols-5 gap-6 mt-6 items-end" }
const _hoisted_5 = { class: "col-span-3" }
const _hoisted_6 = { class: "text-xs text-gray-500" }
const _hoisted_7 = { class: "mt-6 justify-between w-full" }
const _hoisted_8 = { class: "grid grid-cols-5 gap-6 items-end" }
const _hoisted_9 = { class: "col-span-4" }
const _hoisted_10 = { class: "block text-sm font-medium text-gray-700 mb-2" }
const _hoisted_11 = { class: "text-gray-500 text-xs ml-1" }
const _hoisted_12 = { class: "col-span-1" }

import { computed, ref } from 'vue';
import { BaseButton } from '@/lib/components/Button';
import { SelectInput } from '@/lib/components/Select';
import { NumberInput, BaseTextInput } from '@/lib/components/Input';
import { SearchInput } from '@/lib/components/Search';
import { useI18n } from 'vue-i18n';
import { useProductCard } from '@/composables/use-product-card';
import type { LensProperty, ProductImage, Variant, HighlightBgColor } from '@/models/webshop.model';
import AddPatientModal from '@/views/patient/AddPatientModal.vue';
import { useRoute } from 'vue-router';
import { Patient } from '@/models';
import { useQuantityValidation } from '@/composables/use-quantity-validation';
import { translateBoolean } from '@/lib/helpers/string.helper';
import { WEBSHOP_MIN_PRODUCT_QUANTITY, WEBSHOP_MAX_PRODUCT_QUANTITY, WEBSHOP_DROPDOWN_LINE_HEIGHT } from '@/constants';
import BaseAlert from '../Banner/BaseAlert.vue';
import ProductPrimaryInfo from './ProductPrimaryInfo.vue';
import { usePatientPermissions } from '@/composables/use-patient-permissions';



export default /*@__PURE__*/_defineComponent({
  __name: 'ProductCardLens',
  props: {
    mlsId: {},
    productCategory: {},
    productCategorySlug: {},
    productCategoryDisplayName: {},
    title: {},
    image: {},
    lensProperties: {},
    variants: {},
    lensCode: {},
    dataDusk: {},
    highlightText: {},
    highlightColor: {}
  },
  setup(__props: any) {

const props = __props;

const { t } = useI18n();
const route = useRoute();
const { canReadPatient, canCreatePatient } = usePatientPermissions();
const currentOrganisationId = computed(() => route.params.organisationId as string);
const lensProperties = ref(props.lensProperties);
const {
  dynamicParameters,
  displayableProperties,
  dynamicPropertyOptions: baseOptions,
  pack,
  quantity,
  patient,
  patientSearchText,
  patientSuggestions,
  isPatientSearchLoading,
  packOptions,
  isAddToCartDisabled,
  handleAddToCart,
  validateReference,
  reference,
  handlePatientChange,
  isPatientSetting,
  calculateLensProperties,
  isLoadingParameters,
  canBeOrdered,
  invalidPropertyErrors
} = useProductCard({
  mlsId: props.mlsId,
  productName: props.title,
  productCategory: props.productCategory,
  productCategorySlug: props.productCategorySlug,
  productCategoryDisplayName: props.productCategoryDisplayName,
  lensProperties,
  variants: props.variants,
  image: props.image,
  lensCode: props.lensCode,
  organisationId: currentOrganisationId
});

const { validateQuantityInput, handleQuantityChange, handleQuantityBlur } = useQuantityValidation(quantity);
const dynamicPropertyOptions = computed(() => ({
  ...baseOptions.value,
  TrialLens: baseOptions.value.TrialLens?.map((option) => ({
    ...option,
    label: translateBoolean(option.label, t)
  }))
}));

// Check if all parameters are filled (to determine if we should show canBeOrdered warning)
const allParametersFilled = computed(() =>
  displayableProperties.value.every((property) => {
    const options = dynamicPropertyOptions.value[property.name] ?? [];
    // If there are options, the parameter must be filled
    return options.length === 0 || dynamicParameters[property.name] !== null;
  })
);

// Computed to determine if the "combination not available" warning should be shown
const showCombinationNotAvailableWarning = computed(() =>
  !canBeOrdered.value && allParametersFilled.value && !isLoadingParameters.value
);

// Helper to get translation key for property names
const getPropertyLabel = (propertyName: string): string => {
  const key = `platform.webshop.${propertyName.toLowerCase()}`;
  const translated = t(key);
  // If translation is missing, i18n returns the key itself
  if (translated === key) {
    return propertyName
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
      .trim();
  }
  return translated;
};

const getPropertyPlaceholder = (propertyName: string): string => {
  const key = `platform.webshop.placeHolders.${propertyName.toLowerCase()}`;
  const translated = t(key);
  return translated === key ? t('platform.webshop.placeHolders.radius') : translated;
};

const cardDusk = computed(() => props.dataDusk || 'product-card-id');
const showModal = ref(false);
const handlePatientCreated = (newPatient: Patient) => {
  const patientSuggestion = {
    id: newPatient.id,
    value: newPatient.id,
    label: `${newPatient.first_name} ${newPatient.last_name} - ID: ${newPatient.mrn}` ||
           `${newPatient.first_name} ${newPatient.last_name}`
  };
  patient.value = newPatient;
  patientSearchText.value = patientSuggestion.label;
  patientSuggestions.value = [patientSuggestion];
  showModal.value = false;
};

const curSelection = computed(() => {
  const values = Object.values(dynamicParameters);
  const firstFalsyIndex = values.findIndex((value) => !value);
  return firstFalsyIndex === -1 ? values.length : firstFalsyIndex;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "w-full",
    "data-dusk": cardDusk.value
  }, [
    _createVNode(ProductPrimaryInfo, {
      title: props.title,
      image: props.image,
      "highlight-text": props.highlightText,
      "highlight-color": props.highlightColor
    }, null, 8, ["title", "image", "highlight-text", "highlight-color"]),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(displayableProperties), (property, index) => {
          return _withDirectives((_openBlock(), _createElementBlock("div", {
            key: property.name
          }, [
            _createVNode(_unref(SelectInput), {
              modelValue: _unref(dynamicParameters)[property.name],
              "onUpdate:modelValue": [($event: any) => ((_unref(dynamicParameters)[property.name]) = $event), (val) => {
              if (val === null || val === '') return;
              _unref(calculateLensProperties)({ propertyName: property.name, value: val });
            }],
              disabled: !(index <= curSelection.value),
              label: getPropertyLabel(property.name),
              loading: _unref(isLoadingParameters),
              "line-height": _unref(WEBSHOP_DROPDOWN_LINE_HEIGHT),
              placeholder: getPropertyPlaceholder(property.name),
              options: dynamicPropertyOptions.value[property.name] || [],
              clear: true,
              error: _unref(invalidPropertyErrors)[property.name],
              "data-dusk": `${property.name.toLowerCase()}-select`
            }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled", "label", "loading", "line-height", "placeholder", "options", "error", "data-dusk"])
          ])), [
            [_vShow, dynamicPropertyOptions.value[property.name] && dynamicPropertyOptions.value[property.name].length > 0]
          ])
        }), 128))
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", null, [
          _createVNode(_unref(SelectInput), {
            modelValue: _unref(pack),
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(pack) ? (pack).value = $event : null)),
            label: _unref(t)('platform.webshop.pack'),
            disabled: curSelection.value < _unref(displayableProperties).length || _unref(packOptions).length === 0,
            loading: _unref(isLoadingParameters),
            clear: true,
            placeholder: _unref(t)('platform.webshop.placeHolders.pack'),
            options: _unref(packOptions),
            "data-dusk": "pack-select"
          }, null, 8, ["modelValue", "label", "disabled", "loading", "placeholder", "options"])
        ]),
        _createElementVNode("div", null, [
          _createVNode(_unref(NumberInput), {
            modelValue: _unref(quantity),
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(quantity) ? (quantity).value = $event : null)),
            label: _unref(t)('platform.webshop.quantity'),
            placeholder: _unref(t)('platform.webshop.placeHolders.quantity'),
            "data-dusk": "quantity-input",
            min: _unref(WEBSHOP_MIN_PRODUCT_QUANTITY),
            max: _unref(WEBSHOP_MAX_PRODUCT_QUANTITY),
            onKeydown: _unref(validateQuantityInput),
            onInput: _unref(handleQuantityChange),
            onBlur: _unref(handleQuantityBlur)
          }, null, 8, ["modelValue", "label", "placeholder", "min", "max", "onKeydown", "onInput", "onBlur"])
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createVNode(_unref(BaseTextInput), {
            modelValue: _unref(reference),
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_isRef(reference) ? (reference).value = $event : null)),
            label: _unref(t)('platform.webshop.reference'),
            count: 30,
            "is-optional": "",
            placeholder: _unref(t)('platform.webshop.placeHolders.reference'),
            "data-dusk": "reference-input",
            onInput: _unref(validateReference)
          }, {
            suffix: _withCtx(() => [
              _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(reference) ? _unref(reference).length : 0) + "/30", 1)
            ]),
            _: 1
          }, 8, ["modelValue", "label", "placeholder", "onInput"])
        ])
      ]),
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("div", _hoisted_8, [
          _createElementVNode("div", _hoisted_9, [
            (_unref(canReadPatient))
              ? (_openBlock(), _createBlock(_unref(SearchInput), {
                  key: 0,
                  modelValue: _unref(patientSearchText),
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (_isRef(patientSearchText) ? (patientSearchText).value = $event : null)),
                  icon: _unref(patient) !== null ? 'check-square' : 'search',
                  label: _unref(t)('platform.webshop.patient'),
                  placeholder: _unref(t)('platform.webshop.placeHolders.search-patient'),
                  suggestions: _unref(patientSuggestions),
                  loading: _unref(isPatientSearchLoading),
                  clear: true,
                  "is-optional": true,
                  "add-button-link-label": _unref(canCreatePatient) ? _unref(t)('platform.webshop.add-patient') : undefined,
                  "add-button-link": _unref(canCreatePatient) ? 'add-patient' : undefined,
                  "add-button-as-event": _unref(canCreatePatient),
                  "data-dusk": "patient-search-input",
                  onChange: _unref(handlePatientChange),
                  onAddButtonClick: _cache[4] || (_cache[4] = ($event: any) => (showModal.value = true))
                }, null, 8, ["modelValue", "icon", "label", "placeholder", "suggestions", "loading", "add-button-link-label", "add-button-link", "add-button-as-event", "onChange"]))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _createElementVNode("label", _hoisted_10, [
                    _createTextVNode(_toDisplayString(_unref(t)('platform.webshop.patient')) + " ", 1),
                    _createElementVNode("span", _hoisted_11, "(" + _toDisplayString(_unref(t)('platform.common.optional')) + ")", 1)
                  ]),
                  _createVNode(BaseAlert, {
                    color: "alert",
                    icon: "halt",
                    label: _unref(t)('platform.webshop.no-patient-read-permission')
                  }, null, 8, ["label"])
                ], 64))
          ]),
          _createElementVNode("div", _hoisted_12, [
            _createVNode(_unref(BaseButton), {
              size: "large",
              "left-icon": 'add',
              class: "px-6 py-3 bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-2 w-full",
              disabled: _unref(isAddToCartDisabled) || _unref(isPatientSetting) || !_unref(canBeOrdered),
              loading: _unref(isPatientSetting),
              "data-dusk": "add-to-cart-button",
              onClick: _unref(handleAddToCart)
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('platform.webshop.addToCart')), 1)
              ]),
              _: 1
            }, 8, ["disabled", "loading", "onClick"])
          ])
        ])
      ])
    ]),
    (showCombinationNotAvailableWarning.value)
      ? (_openBlock(), _createBlock(BaseAlert, {
          key: 0,
          class: "mt-6",
          color: "danger",
          icon: "halt",
          label: _unref(t)('platform.webshop.combination-not-available')
        }, null, 8, ["label"]))
      : _createCommentVNode("", true),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (showModal.value)
        ? (_openBlock(), _createBlock(AddPatientModal, {
            key: 0,
            "organisation-id": currentOrganisationId.value,
            onClose: _cache[5] || (_cache[5] = ($event: any) => (showModal.value = false)),
            onSuccess: handlePatientCreated
          }, null, 8, ["organisation-id"]))
        : _createCommentVNode("", true)
    ]))
  ], 8, _hoisted_1))
}
}

})